import moment from "moment";
import { WEEK_DAYS } from "constants/eddConfig";

export const buildWeeklySchedule = (rows = []) =>
  WEEK_DAYS.map(({ key }) => {
    const row = (rows || []).find((r) => r.day === key);
    return {
      id: row?.id ?? null,
      day: key,
      start_time: row?.start_time ?? "",
      end_time: row?.end_time ?? "",
      is_active: Boolean(row?.is_active),
    };
  });

const toInputTime = (time) => (time ? time.slice(0, 5) : "");

const toApiTime = (time) => (time && time.length === 5 ? `${time}:00` : time);

export const mapWorkingHoursFromApi = (row) => ({
  id: row?.id ?? null,
  day: row?.dayOfWeek,
  start_time: toInputTime(row?.startTime),
  end_time: toInputTime(row?.endTime),
  is_active: Boolean(row?.active),
});

export const mapWorkingHoursToApi = (row) => ({
  ...(row.id && { id: row.id }),
  dayOfWeek: row.day,
  startTime: toApiTime(row.start_time),
  endTime: toApiTime(row.end_time),
  active: Boolean(row.is_active),
});

// supply service sends / expects off day dates as DD-MM-YYYY
const API_DATE_FORMAT = "DD-MM-YYYY";
const UI_DATE_FORMAT = "YYYY-MM-DD";

export const mapOffDayFromApi = (row) => ({
  id: row?.id ?? null,
  date: row?.offDate
    ? moment(row.offDate, API_DATE_FORMAT).format(UI_DATE_FORMAT)
    : null,
  is_working: Boolean(row?.isWorking),
  start_time: toInputTime(row?.startTime) || null,
  end_time: toInputTime(row?.endTime) || null,
  reason: row?.reason ?? "",
  is_active: Boolean(row?.active),
});

export const mapOffDayToApi = (row) => ({
  ...(row.id && { id: row.id }),
  offDate: moment(row.date, UI_DATE_FORMAT).format(API_DATE_FORMAT),
  isWorking: Boolean(row.is_working),
  startTime: row.is_working ? toApiTime(row.start_time) : null,
  endTime: row.is_working ? toApiTime(row.end_time) : null,
  reason: row.reason?.trim() ?? "",
  active: Boolean(row.is_active),
});

export const getRowResultToast = ({ successCount, failedCount }, action) => {
  if (!failedCount) return [`${action} successful`, "success"];
  if (!successCount)
    return [`${action} failed for all ${failedCount} rows`, "error"];
  return [`${successCount} rows saved, ${failedCount} rows failed`, "warning"];
};

export const isSameScheduleRow = (a = {}, b = {}) =>
  a.start_time === b.start_time &&
  a.end_time === b.end_time &&
  a.is_active === b.is_active;

/**
 * @description rows edited since last save. A day never saved and still
 * inactive has nothing to persist, so it is skipped.
 */
export const getChangedScheduleRows = (schedule = [], savedSchedule = []) =>
  schedule.filter((row) => {
    const savedRow = savedSchedule.find((r) => r.day === row.day);
    if (isSameScheduleRow(row, savedRow)) return false;
    return Boolean(row.id) || row.is_active;
  });

// same formats as master data admin (utils/vendor_master/Validation.ts)
export const VENDOR_KEY_REGEX = /^V\d{4}-\d{4}-[A-Z]{4}$/;
export const VENDOR_LOCATION_KEY_REGEX = /^V\d{4}-\d{4}-[A-Z]{4}-\d{4}$/;

export const isValidVendorId = (id = "") => {
  const value = id.trim().toUpperCase();
  return VENDOR_KEY_REGEX.test(value) || VENDOR_LOCATION_KEY_REGEX.test(value);
};

export const mapVendorSearchToLocations = (results = []) =>
  results.flatMap((item) =>
    (item?.gst || []).flatMap((gst) =>
      (gst?.locations || []).map((loc) => ({
        vendor_id: loc.vendor_location_key,
        vendor_key: gst.vendor_key,
        vendor_name: gst.display_name,
        gst_number: gst.gst_number,
        location_name: loc.location_display_name,
        city: loc.address?.city ?? loc.city,
        state: loc.address?.state ?? loc.state,
      })),
    ),
  );

const toMinutes = (time) => {
  if (!time) return null;
  const [h, m] = time.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

/**
 * @description minutes between start & end ("HH:mm"); 0 when invalid or end <= start
 */
export const getDurationInMinutes = (start, end) => {
  const startMin = toMinutes(start);
  const endMin = toMinutes(end);
  if (startMin === null || endMin === null || endMin <= startMin) return 0;
  return endMin - startMin;
};

/**
 * @description 540 -> "9h", 570 -> "9h 30m", 0 -> "—"
 */
export const formatDuration = (minutes) => {
  if (!minutes) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

export const getWeeklyTotalHours = (schedule = []) =>
  schedule.reduce(
    (sum, row) =>
      row.is_active
        ? sum + getDurationInMinutes(row.start_time, row.end_time)
        : sum,
    0,
  ) / 60;

export const validateOffDay = (offDay) => {
  if (!offDay.date) return "Date is required";
  if (offDay.is_working === null || offDay.is_working === undefined)
    return "Please select if vendor is working";
  if (offDay.is_working) {
    if (!offDay.start_time || !offDay.end_time)
      return "Start and end time required for partial closure";
    if (toMinutes(offDay.end_time) <= toMinutes(offDay.start_time))
      return "End time must be after start time";
  }
  if (!offDay.reason?.trim()) return "Reason is required";
  return null;
};

export const validateScheduleRow = (row) => {
  if (!row.is_active) return null;
  if (!row.start_time || !row.end_time) return "Start and end time required";
  if (toMinutes(row.end_time) <= toMinutes(row.start_time))
    return "End time must be after start time";
  return null;
};

const EDD_MASTER_PERMISSIONS = [
  "edd-master",
  "edd_master",
  "edd master",
  "role_edd_master",
];

export const checkHasEddMasterAccess = (userData) => {
  if (!userData) return false;

  const matchesPermission = (item) => {
    if (!item) return false;
    const name =
      typeof item === "string"
        ? item
        : item?.name || item?.label || item?.code || "";
    if (typeof name !== "string") return false;
    const normalized = name.trim().toLowerCase();
    return (
      EDD_MASTER_PERMISSIONS.includes(normalized) ||
      normalized.replace(/[\s_-]+/g, "") === "eddmaster" ||
      normalized.replace(/[\s_-]+/g, "") === "roleeddmaster"
    );
  };

  const hasRole =
    Array.isArray(userData.roles) && userData.roles.some(matchesPermission);
  const hasModule =
    Array.isArray(userData.modules) && userData.modules.some(matchesPermission);

  return Boolean(hasRole || hasModule);
};

